/**
 * Role and capability types for the permissions matrix.
 *
 * Roles mirror the five org roles configured in Clerk. All five are org-local,
 * including `org:global_admin`, which a customer's own top admin holds.
 * Careforce staff are recognised by identity rather than by a role here.
 */

export const ROLES = [
  { key: 'org:global_admin', label: 'Global Admin' },
  { key: 'org:admin_phi', label: 'Admin PHI' },
  { key: 'org:admin', label: 'Admin' },
  { key: 'org:member_phi', label: 'Member PHI' },
  { key: 'org:member', label: 'Member' },
] as const

export type RoleKey = (typeof ROLES)[number]['key']

/**
 * The non-PHI half of the clearance axis. Admin level and PHI clearance are
 * independent, so these administer without being cleared to read patient data.
 */
export const ROLES_WITHOUT_PHI_CLEARANCE: RoleKey[] = [
  'org:admin',
  'org:member',
]

/**
 * The admin half of the hierarchy. Must match the role list in
 * `duty_access_ok()`, which exempts these roles from duty access lists.
 * member_phi is deliberately absent: PHI clearance is not admin level.
 */
export const ADMIN_ROLES: RoleKey[] = [
  'org:global_admin',
  'org:admin_phi',
  'org:admin',
]

export const ALL_ROLE_KEYS: RoleKey[] = ROLES.map((r) => r.key)

export function roleLabel(key: RoleKey): string {
  return ROLES.find((r) => r.key === key)?.label ?? key
}

/** A single gateable capability: one row of the matrix. */
export interface Capability {
  /** Stable identifier used as the row key and as the save-payload key. */
  id: string
  /** Sidebar section the surface belongs to. */
  section: string
  /** Page or surface name as it appears in the app. */
  page: string
  /** Route, or null for surfaces with no route (agent tools, API-only paths). */
  route: string | null
  /** True when the surface exposes health information identifying a patient. */
  phi: boolean
  /** What the role is being granted. */
  capability: string
  /**
   * True when exercising it puts the underlying data in front of the holder.
   *
   * On a PHI row this decides who may hold it: an import writes patient data
   * without displaying any, which is why an administrator without PHI
   * clearance can legitimately be ticked onto one.
   */
  read: boolean
  /** True when it changes stored state or triggers a side effect. */
  write: boolean
  /**
   * True on the row that IS the page, which the others nest under.
   *
   * Marked rather than inferred: reading it off which row the server happens
   * to guard put Duty detail's two dozen controls under "Compiled templates".
   */
  pageRow?: boolean
  /** Roles permitted to use it. */
  roles: RoleKey[]
}

/** Roles keyed by capability id — the shape the matrix edits and saves. */
export type PermissionSelection = Record<string, RoleKey[]>

/**
 * A capability as the table renders it, carrying the values being edited.
 *
 * The live selection rides on the row rather than in a closure over the column
 * defs: TanStack takes `columns` straight into the table, so rebuilding them
 * on every edit remounts each cell and drops what the operator was doing.
 */
export interface MatrixRow extends Capability {
  /** Orgs holding this capability. Absent outside the Careforce scope. */
  orgs?: string[]
  /**
   * True on every row of a page no role can open.
   *
   * The page is gone from the sidebar and its route is refused, so nothing on
   * it can be exercised until the governing row grants somebody.
   */
  pageHidden?: boolean
  /**
   * True on a row that sits under the one governing its page.
   *
   * An import is reached from the page it imports into, so it is listed
   * beneath the read that opens that page rather than beside it.
   */
  dependent?: boolean
  /** The page this row belongs to, as the expand/collapse state keys it. */
  pageKey?: string
  /** How many rows sit under this one. Set on a governing row that has any. */
  childCount?: number
  /** Every row on this page, this one included. Set on the page row. */
  pageIds?: string[]
  /** Roles every row on the page holds — the page row's ticked boxes. */
  pageRolesAll?: RoleKey[]
  /** Roles at least one row holds. Those not in `pageRolesAll` show mixed. */
  pageRolesSome?: RoleKey[]
  /** Roles every row on the page forbids, so the page row cannot offer them. */
  pageRolesLocked?: RoleKey[]
  /**
   * Every org any row on the page reaches.
   *
   * A union rather than the page row's own scope, so the header cannot say
   * Careforce only above a row that says All orgs.
   */
  pageOrgs?: string[]
  /** Whether this row's dependents are currently listed. */
  expanded?: boolean
}
